import { IonContent, isPlatform } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { FormInput } from '@/components/FormInput';
import { UiTypography } from '@/components/ui/Typography';
import { useAccount } from '@/hooks/useAccount';
import { useCreateStripeClient } from '@/hooks/useCreateStripeClient';
import { amplitudeModule } from '@/modules/amplitude';
import { ApiBotType } from '@/modules/api';
import { webCampaign } from '@/modules/webCampaign';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import { OnboardingSubtitle } from '@/pages/Onboarding/components/Subtitle';
import { OnboardingTitle } from '@/pages/Onboarding/components/Title';
import { AI_DATA } from '@/pages/Onboarding/constants';
import { useQueryPersonalizedAis } from '@/pages/Onboarding/hooks/useQueryPersonalizedAis';
import { checkIsValidateEmail } from '@/pages/Onboarding/utils/checkIsValidEmail';
import { sliceTwins } from '@/pages/Onboarding/utils/sliceTwins';
import { userService } from '@/services/user';
import { trackOnboardingComplete } from '@/utils/analytics';
import { canLogEvents } from '@/utils/canLogEvents';
import { preloadImage } from '@/utils/preloadImage';

import { layoutQueryParams } from '../../params';
import { LayoutDataType } from '../../types';

import { EmailStyles } from './Email.styles';

export const ScreenEmail: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Email',
      });
    }

    layoutQueryParams.removeStepsData();

    const aiData = AI_DATA[webCampaign.aiType];

    aiData.cards.forEach((card) => preloadImage(card.imageSrc));

    preloadImage(aiData.main.imageSrc);
    preloadImage(
      'https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/onboarding/paywall/bg-line.png',
    );

    if (window.innerWidth >= 768) {
      preloadImage(
        'https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/onboarding/paywall/bg-desktop.png',
      );
    } else {
      preloadImage(
        'https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/onboarding/paywall/bg-mobile.png',
      );
    }
  }, []);

  const form = useFormContext<LayoutDataType>();

  const { refreshUser } = useAccount();

  const inputRef = React.useRef<HTMLIonInputElement>(null);

  const formCharacters = form.watch('characters');
  const formEmail = form.watch('email');

  const isValidEmail = checkIsValidateEmail(formEmail);

  const { createStripeClient, isCreatingStripeClient } =
    useCreateStripeClient();

  React.useEffect(() => {
    if (isPlatform('android') || isPlatform('desktop')) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.setFocus();
        }
      });
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.setFocus();
        }
      }, 1000);
    }
  }, []);

  const [twinsData, setTwinsData] = React.useState<ApiBotType[]>(
    formCharacters || [],
  );
  const sicedTwins = sliceTwins(twinsData || []);

  useQueryPersonalizedAis({
    enabled: !twinsData.length || !formCharacters.length,
    form,
    onSuccess: (data) => {
      setTwinsData(data.twins);
    },
  });

  const handleNextStep = async (): Promise<void> => {
    if (!formEmail || !isValidEmail) return;

    try {
      const lowerCaseEmail = formEmail.toLowerCase().trim();

      if (canLogEvents()) {
        trackOnboardingComplete({ email: lowerCaseEmail });
      }

      await createStripeClient(lowerCaseEmail);
      await userService.createOrUpdate({
        onboarding_data: {
          onboarded: true,
        },
      });

      refreshUser();
    } catch (error) {
      console.error('Can not create a stripe user', error);
    }
  };

  return (
    <EmailStyles>
      <OnboardingNavigation />

      <IonContent>
        <OnboardingTitle>
          We’ve added{' '}
          <UiTypography variant="inherit" component="span" color="danger">
            1578
          </UiTypography>{' '}
          AI characters
        </OnboardingTitle>
        <OnboardingSubtitle>customized to your preferences</OnboardingSubtitle>

        <div className="images-container">
          <div className="images-view">
            {sicedTwins.map((twinArr, idx) => (
              <div className="images-view-line" key={idx}>
                {twinArr.map((twin) => (
                  <img src={twin.profile_picture_url} alt="AI" key={twin.id} />
                ))}
              </div>
            ))}
          </div>
        </div>
        <OnboardingTitle className="email-title">
          Enter your email to create a personal account
        </OnboardingTitle>

        <div className="email-input-container">
          <FormInput
            form={form}
            hidden={false}
            onSubmit={handleNextStep}
            disableButton={!isValidEmail}
            ref={inputRef}
            name="email"
            type="email"
            placeholder="Type your email"
            loading={isCreatingStripeClient}
          />
          <UiTypography
            className="email-disclaimer"
            variant="label-medium"
            color="neutral-black"
          >
            We need your email to save your preferences and get you chatting
            right away. You can update them anytime later.
          </UiTypography>
        </div>
      </IonContent>
    </EmailStyles>
  );
};
